import { DotLegalCheckbox, DotLegalDialog, DotLegalHeader, DotLegalSelect, GridSizes } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import useTransferResponsibilityDialogHooks from "./TransferResponsibilityDialog.hooks";
import { UserResponsibilityEntityType } from "../../reviewUserResponsibility/ReviewUserResponsibility.type";
import { ITransferUserResponsibility } from "../TransferUserResponsibility";
import { TransferUserResponsibilityModel } from "../TransferUserResponsibility.types";

export interface ITransferResponsibilityDialog extends ITransferUserResponsibility {
    onDialogClose: () => void;
}

const transferCheckboxItems: Array<{
    property: keyof TransferUserResponsibilityModel;
    entityType: UserResponsibilityEntityType;
}> = [
    { property: "transferProcessingActivities", entityType: UserResponsibilityEntityType.ProcessingActivity },
    { property: "transferSystems", entityType: UserResponsibilityEntityType.System },
    { property: "transferLegalEntities", entityType: UserResponsibilityEntityType.LegalEntity },
    { property: "transferPolicies", entityType: UserResponsibilityEntityType.Policy },
    { property: "transferAnnualWheelActivities", entityType: UserResponsibilityEntityType.AnnualWheelActivity },
    { property: "transferTasks", entityType: UserResponsibilityEntityType.Task },
    { property: "transferLegalEntityAudits", entityType: UserResponsibilityEntityType.LegalEntityAudit },
    { property: "transferAuditGroups", entityType: UserResponsibilityEntityType.LegalEntityAuditGroup },
    { property: "transferInternalAssessments", entityType: UserResponsibilityEntityType.InternalAssessment },
    { property: "transferLegalEntityDocuments", entityType: UserResponsibilityEntityType.LegalEntityDocument },
    { property: "transferContracts", entityType: UserResponsibilityEntityType.Contract },
    { property: "transferContractDocuments", entityType: UserResponsibilityEntityType.ContractDocument },
    { property: "transferSharings", entityType: UserResponsibilityEntityType.Sharing },
    { property: "transferPublicInternalAssessmentTemplates", entityType: UserResponsibilityEntityType.PublicInternalAssessmentTemplate },
    { property: "transferFrameworks", entityType: UserResponsibilityEntityType.Framework },
    { property: "transferObligations", entityType: UserResponsibilityEntityType.Obligation },
    { property: "transferRiskMitigationPlans", entityType: UserResponsibilityEntityType.RiskMitigationPlan },
    { property: "transferRiskAreas", entityType: UserResponsibilityEntityType.RiskArea },
    { property: "transferInternalAssessmentGroups", entityType: UserResponsibilityEntityType.InternalAssessmentGroup },
    { property: "transferIncidentLogEntries", entityType: UserResponsibilityEntityType.IncidentLogEntry },
    { property: "transferProcesses", entityType: UserResponsibilityEntityType.Process },
];

function TransferResponsibilityDialog(props: ITransferResponsibilityDialog) {
    const { translateString, translateUserResponsibilityEntityType } = useTranslation();
    const {
        fromUsers,
        toUsers,
        onUserChange,
        onTransferChange,
        transferModel,
        transferResponsible,
        transferButtonEnabled,
        inProgress,
        isTransferCheckboxVisible,
    } = useTransferResponsibilityDialogHooks(props);

    const gridSizes: GridSizes = {
        sm: 12,
        md: 6,
    };

    return (
        <DotLegalDialog
            okButtonDisabled={!transferButtonEnabled}
            size="sm"
            header={props.header ?? translateString("transferReponsibility")}
            buttonOkText={translateString("transfer")}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={() => transferResponsible()}
            inProgress={inProgress}
            disableBackdropOnCloseIfValueChange={transferModel}
        >
            <Box sx={{ marginLeft: 2, marginRight: 2 }}>
                <DotLegalHeader center fontWeight={0} headerStyle={"small"}>
                    {translateString("transferUserResponsibilityInfo")}
                </DotLegalHeader>

                <Box>
                    <DotLegalSelect
                        options={fromUsers}
                        onChange={(user) => onUserChange("fromUser", user)}
                        placeholder={translateString("from")}
                        label={translateString("from")}
                        noOptionsLabel={""}
                        selectedItem={transferModel.fromUser}
                        disabled={props.fromUserId !== undefined}
                    />
                    <DotLegalSelect
                        options={toUsers}
                        onChange={(user) => onUserChange("toUser", user)}
                        placeholder={translateString("to")}
                        label={translateString("to")}
                        noOptionsLabel={""}
                        selectedItem={transferModel.toUser}
                    />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    {props.userResponsibilityEntityType === undefined && (
                        <Grid container>
                            {transferCheckboxItems
                                .filter((item) => isTransferCheckboxVisible(item.entityType))
                                .map((item) => ({
                                    ...item,
                                    label: translateUserResponsibilityEntityType(item.entityType),
                                }))
                                .sort((a, b) => a.label.localeCompare(b.label))
                                .map((item) => (
                                    <Grid key={item.property} size={{ ...gridSizes }}>
                                        <DotLegalCheckbox
                                            onChange={(checked) => onTransferChange(item.property, checked)}
                                            checked={transferModel[item.property] as boolean}
                                            label={item.label}
                                        />
                                    </Grid>
                                ))}
                        </Grid>
                    )}
                </Box>
            </Box>
        </DotLegalDialog>
    );
}

export default TransferResponsibilityDialog;
